import React, {Component} from 'react';
import {
  AccountCircle, CheckCircle, Cancel, Email as EmailIcon,
  Badge, Business, CalendarMonth
} from '@mui/icons-material';
import {
  Avatar, Card, CardContent, Chip, CircularProgress, Divider, Typography
} from '@mui/material';

import Api from '../../api';
import {UI_API_URL_PATH_NEW} from '../../lib/constant';
import {getFromLocalStorage} from '../../lib/utils';

export default class MyAccount extends Component {
  state = {
    profile: null,
    loading: true,
    error: null
  }

  componentDidMount() {
    this.fetchProfile();
  }

  fetchProfile = () => {
    this.setState({loading: true, error: null});
    Api.get(`${UI_API_URL_PATH_NEW}/api/v2/iam/me`)
      .then(response => {
        const profile = response?.data?.data;
        this.setState({profile, loading: false});
      })
      .catch(err => {
        console.error('Failed to fetch profile:', err);
        this.setState({error: 'Failed to load profile', loading: false});
      });
  }

  formatState = (state) => {
    if (!state) return 'Unknown';
    return state.replace('USER_STATE_', '').replace(/_/g, ' ')
      .toLowerCase().replace(/^\w/, c => c.toUpperCase());
  }

  render() {
    const {profile, loading, error} = this.state;

    if (loading) {
      return (
        <div className='sms-parent-container' style={{display: 'flex', justifyContent: 'center', padding: 60}}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <div className='sms-parent-container' style={{padding: 40, textAlign: 'center'}}>
          <Typography color="error">{error}</Typography>
        </div>
      );
    }

    if (!profile) return null;

    // /api/v2/iam/me leaves org_name empty, so the business name comes from the
    // login session (org_name claim of the access token — the same value the
    // header renders). The org id stays visible in small print for support.
    const session = getFromLocalStorage() || {};
    const orgName = profile.orgName || session.instituteName || session.tenantName || '';
    const orgId = profile.orgId || session.tenantId || '';

    return (
      <div className='sms-parent-container' style={window.isMobile() ? {padding: 8} : {}}>
        <Card variant="outlined" sx={{maxWidth: 600, mx: 'auto', mt: 2}}>
          <CardContent>
            <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24}}>
              <Avatar sx={{width: 64, height: 64, bgcolor: '#1976d2', fontSize: 28}}>
                {profile.displayName ? profile.displayName.charAt(0).toUpperCase() : <AccountCircle />}
              </Avatar>
              <div>
                {/* Business name is the headline: customers identify the login
                    by their company, the person's name is secondary. */}
                {orgName && (
                  <Typography variant="h5" fontWeight={600} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <Business fontSize="small" color="action" />{orgName}
                  </Typography>
                )}
                <Typography variant={orgName ? 'subtitle1' : 'h5'} fontWeight={500}>
                  {profile.displayName || profile.username}
                </Typography>
              </div>
            </div>

            <Divider sx={{mb: 2}} />

            <InfoRow icon={<EmailIcon fontSize="small" />} label="Email" value={
              <span>
                {profile.email || '-'}
                {profile.email && (
                  profile.emailVerified
                    ? <CheckCircle sx={{fontSize: 14, ml: 0.5, color: 'success.main', verticalAlign: 'middle'}} />
                    : <Cancel sx={{fontSize: 14, ml: 0.5, color: 'error.main', verticalAlign: 'middle'}} />
                )}
              </span>
            } />

            <InfoRow icon={<Business fontSize="small" />} label="Organization" value={
              <span>
                {orgName || orgId || '-'}
                {orgName && orgId && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 1}}>
                    ID {orgId}
                  </Typography>
                )}
              </span>
            } />

            <InfoRow icon={<Badge fontSize="small" />} label="Status"
              value={<Chip label={this.formatState(profile.state)} size="small"
                color={profile.state === 'USER_STATE_ACTIVE' ? 'success' : 'default'} />}
            />

            {profile.createdAt && (
              <InfoRow icon={<CalendarMonth fontSize="small" />} label="Member Since"
                value={new Date(profile.createdAt).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})} />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
}

const InfoRow = ({icon, label, value}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12}}>
    {icon}
    <Typography variant="body2" color="text.secondary" sx={{minWidth: 100}}>{label}</Typography>
    <Typography variant="body2" component="div">{value}</Typography>
  </div>
);
